import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';
import { FaFacebook, FaMedium, FaTwitter, FaMapMarkedAlt, FaBrain, FaMobileAlt, FaGlobe, FaShieldAlt, FaRocket, FaUsers, FaClock, FaCheckCircle } from 'react-icons/fa';
import ReactGA from 'react-ga4';
import { useLocation } from 'react-router-dom';
import Header from './components/Header';
import IPadMockup from './components/IPadMockup';

const SUPPORT_EMAIL = 'support@idealscribe.com';

function Home() {
  const [showScroll, setShowScroll] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });

  const location = useLocation();

  useEffect(() => {
    ReactGA.send({ hitType: 'pageview', page: location.pathname + location.search });
  }, [location]);
  
  const checkScrollTop = () => {
    if (!showScroll && window.pageYOffset > 400) {
      setShowScroll(true);
    } else if (showScroll && window.pageYOffset <= 400) {
      setShowScroll(false);
    }
  };

  const scrollTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    window.addEventListener('scroll', checkScrollTop);
    return () => {
      window.removeEventListener('scroll', checkScrollTop);
    };
  }, [showScroll]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const { name, email, message } = formData;
    const mailtoLink = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Ideal Scribe — Contact form')}&body=Name:%20${encodeURIComponent(name)}%0AEmail:%20${encodeURIComponent(email)}%0AMessage:%20${encodeURIComponent(message)}`;
    window.location.href = mailtoLink;
  };

  return (
    <div className="home">
      <Header currentPage="home" />

      <div className="content" style={{ marginTop: '70px' }}>
        {/* Hero Section */}
        <section className="hero">
          <div className="hero-content">
            <div className="hero-text">
              <h1 className="hero-title">
                Transform Your Ideas Into
                <span className="hero-highlight"> Organized Adventures</span>
              </h1>
              <p className="hero-subtitle">
                The intelligent note-taking platform that helps you capture, organize, and plan your next adventure with AI-powered insights and seamless organization.
              </p>
              <div className="hero-cta">
                <Link to="/app">
                  <button className="hero-button-primary">Start Creating Notes</button>
                </Link>
              </div>
              <div className="hero-stats">
                <div className="stat-item">
                  <span className="stat-number">10,000+</span>
                  <span className="stat-label">Notes Created</span>
                </div>

                <div className="stat-item">
                  <span className="stat-number">24/7</span>
                  <span className="stat-label">AI Processing</span>
                </div>
              </div>
            </div>
            <div className="hero-visual">
              <div className="hero-mockup">
                <IPadMockup />
              </div>
            </div>
          </div>
        </section>

        {/* Value Proposition Section */}
        <section className="value-prop">
          <div className="container">
            <div className="section-header">
              <h2>Why Choose Ideal Scribe?</h2>
              <p>A simple way to organize and activate your ideas</p>
            </div>
            <div className="value-grid">
              <div className="value-card">
                <div className="value-icon">
                  <FaBrain />
                </div>
                <h3>AI-Powered Organization</h3>
                <p>Never forget a quick travel tip or idea again with Quick Notes.</p>
                <p>Automatically categorize and tag your notes with intelligent AI that understands context and content.</p>
              </div>
              <div className="value-card">
                <div className="value-icon">
                  <FaMapMarkedAlt />
                </div>
                <h3>Explorer Mode</h3>
                <p>Easily track, organize, and visualize your notes and bucket list on interactive maps and discover connections between places and ideas.</p>
              </div>
              <div className="value-card">
                <div className="value-icon">
                  <FaMobileAlt />
                </div>
                <h3>Automated Trip Planning</h3>
                <p>Automatically generate a customized travel plan without having to add everything manually and access it from anywhere in minutes.</p>
              </div>
              
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="how-it-works">
          <div className="container">
            <div className="section-header">
              <h2>How It Works</h2>
            </div>
            <div className="steps-grid">
              <div className="step-card">
                <div className="step-number">1</div>
                <h3>Create Your Note</h3>
                <p>Start with a simple title and description. Our intuitive interface makes note-taking effortless.</p>
              </div>
              <div className="step-card">
                <div className="step-number">2</div>
                <h3>AI Processing</h3>
                <p>Our AI analyzes your content and automatically categorizes, tags, and organizes your notes.</p>
              </div>
              <div className="step-card">
                <div className="step-number">3</div>
                <h3>Discover & Explore</h3>
                <p>Find your notes easily with smart search, filters, and visual organization tools.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Your Interests First - Full Width */}
        <section className="interests-first-section">
          <div className="interests-first-content">
            <h2>Your Privacy First</h2>
            <p>We chose a different model, one that never puts affiliate interests or paid promotions ahead of you. Your data stays with you and is used to personalize recommendations just for you, not third parties.</p>
          </div>
        </section>

        {/* CTA Section */}
        <section className="cta-section">
          <div className="container">
            <div className="cta-content">
              <h2>Turn your idea into your next adventure</h2>
              <p>Discover the fastest way to start planning your trip,<br />from inspiration to itinerary</p>
              <div className="cta-buttons">
                <Link to="/app">
                  <button className="cta-button-primary">Start Free Today</button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="contact">
          <div className="container">
            <div className="contact-content">
              <div className="contact-info">
                <h2>Get in Touch</h2>
                <p>
                  Questions before you sign up? Email{' '}
                  <a className="contact-email-link" href={`mailto:${SUPPORT_EMAIL}`}>
                    {SUPPORT_EMAIL}
                  </a>
                  , or use the form and we will get back to you as soon as we can.
                </p>
                <div className="contact-details">
                  <div className="contact-item">
                    <FaUsers />
                    <span>24/7 Support</span>
                  </div>
                  <div className="contact-item">
                    <FaClock />
                    <span>Quick Response</span>
                  </div>
                  <div className="contact-item">
                    <FaGlobe />
                    <span>Global Community</span>
                  </div>
                </div>
              </div>
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <input 
                    type="text" 
                    name="name" 
                    placeholder="Your Name" 
                    className="contact-input" 
                    value={formData.name} 
                    onChange={handleInputChange} 
                    required
                  />
                </div>
                <div className="form-group">
                  <input 
                    type="email" 
                    name="email" 
                    placeholder="Your Email" 
                    className="contact-input" 
                    value={formData.email} 
                    onChange={handleInputChange} 
                    required
                  />
                </div>
                <div className="form-group">
                  <textarea 
                    name="message" 
                    placeholder="Your Message" 
                    className="contact-textarea" 
                    value={formData.message} 
                    onChange={handleInputChange}
                    required
                  ></textarea>
                </div>
                <button type="submit" className="contact-button">Send Message</button>
              </form>
            </div>
          </div>
        </section>
      </div>

      <footer className="home-footer">
        <div className="footer-content">
          <div className="footer-left">
            <span>© {new Date().getFullYear()} Ideal Scribe. All rights reserved.</span>
          </div>
          <nav className="footer-links">
            <Link to="/terms-of-service">Terms of Service</Link>
            <Link to="/privacy-policy">Privacy Policy</Link>
            <Link to="/support">Support</Link>
            <div className="social-links">
              <a href="https://medium.com" target="_blank" rel="noopener noreferrer"><FaMedium /></a>
              <a href="https://www.facebook.com" target="_blank" rel="noopener noreferrer"><FaFacebook /></a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer"><FaTwitter /></a>
            </div>
          </nav>
        </div>
      </footer>

      {showScroll && (
        <button className="scroll-to-top" onClick={scrollTop}>
          ↑
        </button>
      )}
    </div>
  );
}

export default Home;
